import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { SiteOccupancy } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { t } from '../../lib/i18n';
import { useCan } from '../../lib/session';
import { ErrorLine, ErrorPanel } from '../../components/ErrorPanel';
import { CapacityBar } from '../../components/phase3';
import { Alert, Button, Card, CheckboxField, SelectField, Spinner, TextField } from '../../components/ui';
import { useScopedSettings } from '../sites/api';

interface Draft {
  capacity?: number;
  amberPercent?: number;
  redPercent?: number;
  whenFull?: 'off' | 'warn' | 'block_visitors';
  countAssumed?: boolean;
}

const whole = (v: string): number => Math.max(0, Math.floor(Number(v) || 0));

/**
 * Site card "Capacity" (occupancy.md section 6): the safe maximum, the amber and red levels, what happens when full and whether
 * sign-out-first members count. Saves through the site settings route (sites.manage). W23 mounts it on the site page.
 */
export function CapacityCard({ siteId }: { siteId: string }) {
  const canEdit = useCan('sites.manage');
  const canSee = useCan('occupancy.view');
  const qc = useQueryClient();
  const settings = useScopedSettings(`/sites/${siteId}/settings`, 'occupancy');
  const [draft, setDraft] = useState<Draft>({});
  const live = useQuery({
    queryKey: ['occupancy', 'site', siteId],
    enabled: canSee,
    queryFn: () => api.get(`/occupancy/sites/${siteId}`, { schema: SiteOccupancy }),
  });

  if (settings.query.isPending) return <Spinner />;
  if (settings.query.isError) return <ErrorPanel error={settings.query.error} onRetry={() => void settings.query.refetch()} />;
  const saved = settings.values ?? {};
  const value = <K extends keyof Draft>(k: K): NonNullable<Draft[K]> => (draft[k] ?? saved[k]) as NonNullable<Draft[K]>;
  const changed = Object.keys(draft).length > 0;
  const levelsWrong = value('capacity') > 0 && value('redPercent') < value('amberPercent');

  return (
    <Card
      title={t('siteF.capacity.title')}
      description={t('siteF.capacity.description')}
      footer={
        canEdit ? (
          <>
            <Button disabled={!changed} onClick={() => setDraft({})}>
              {t('settings.discard')}
            </Button>
            <Button
              variant="primary"
              disabled={!changed || levelsWrong || settings.save.isPending}
              onClick={() => settings.save.mutate(draft as Record<string, unknown>, { onSuccess: () => { setDraft({}); void qc.invalidateQueries({ queryKey: ['occupancy'] }); } })}
            >
              {t('common.save')}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <ErrorLine error={settings.save.error} />
        {live.data ? <CapacityBar label={live.data.siteName} current={live.data.count} capacity={live.data.capacity ?? 0} /> : null}
        <TextField
          label={t('siteF.capacity.capacity')}
          hint={t('siteF.capacity.capacityHint')}
          type="number"
          min={0}
          disabled={!canEdit}
          value={String(value('capacity'))}
          onChange={(e) => setDraft({ ...draft, capacity: whole(e.target.value) })}
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label={t('siteF.capacity.amber')}
            type="number"
            min={1}
            max={200}
            disabled={!canEdit}
            value={String(value('amberPercent'))}
            onChange={(e) => setDraft({ ...draft, amberPercent: whole(e.target.value) })}
          />
          <TextField
            label={t('siteF.capacity.red')}
            type="number"
            min={1}
            max={200}
            disabled={!canEdit}
            value={String(value('redPercent'))}
            onChange={(e) => setDraft({ ...draft, redPercent: whole(e.target.value) })}
          />
        </div>
        {levelsWrong ? <Alert>{t('siteF.capacity.levelsWrong')}</Alert> : null}
        <SelectField
          label={t('siteF.capacity.whenFull')}
          hint={t('siteF.capacity.whenFullHint')}
          disabled={!canEdit}
          value={value('whenFull')}
          onChange={(e) => setDraft({ ...draft, whenFull: e.target.value as NonNullable<Draft['whenFull']> })}
        >
          <option value="off">{t('siteF.capacity.when.off')}</option>
          <option value="warn">{t('siteF.capacity.when.warn')}</option>
          <option value="block_visitors">{t('siteF.capacity.when.block_visitors')}</option>
        </SelectField>
        <CheckboxField
          label={t('siteF.capacity.countAssumed')}
          hint={t('siteF.capacity.countAssumedHint')}
          disabled={!canEdit}
          checked={value('countAssumed')}
          onChange={(e) => setDraft({ ...draft, countAssumed: e.target.checked })}
        />
      </div>
    </Card>
  );
}
